import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Search, Package, ChefHat, CheckCircle, Clock, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TrackOrderPage = () => {
  const { orderId: urlOrderId } = useParams();
  const [orderId, setOrderId] = useState(urlOrderId || "");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchOrder = useCallback(async (id) => {
    if (!id) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/orders/${id.toUpperCase()}`);
      setOrder(response.data);
    } catch (error) {
      console.error("Failed to fetch order:", error);
      toast.error("Order not found. Please check your order ID.");
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (urlOrderId) {
      fetchOrder(urlOrderId);
    }
  }, [urlOrderId, fetchOrder]);

  // Auto-refresh every 30 seconds if order is not completed
  useEffect(() => {
    if (order && order.status !== "completed" && order.status !== "cancelled") {
      const interval = setInterval(() => {
        setRefreshing(true);
        fetchOrder(order.order_id).finally(() => setRefreshing(false));
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [order, fetchOrder]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchOrder(orderId);
  };

  const handleRefresh = () => {
    if (order) {
      setRefreshing(true);
      fetchOrder(order.order_id).finally(() => setRefreshing(false));
    }
  };

  const getStatusIndex = (status) => {
    const statuses = ["placed", "received", "processing", "ready", "completed"];
    return statuses.indexOf(status);
  };

  const statusConfig = {
    placed: {
      icon: <Package className="w-6 h-6" />,
      label: "Order Placed",
      description: "Your order has been placed. We'll confirm it shortly.",
      color: "bg-blue-600"
    },
    received: {
      icon: <Package className="w-6 h-6" />,
      label: "Order Confirmed",
      description: "We've confirmed your order and it's in our queue.",
      color: "bg-[#A61B1B]"
    },
    processing: {
      icon: <ChefHat className="w-6 h-6" />,
      label: "Being Prepared",
      description: "Our butchers are preparing your fresh cuts.",
      color: "bg-[#E67E22]"
    },
    ready: {
      icon: <CheckCircle className="w-6 h-6" />,
      label: "Ready for Pickup",
      description: "Your order is ready! Come to the meat counter.",
      color: "bg-[#4A7C59]"
    },
    completed: {
      icon: <CheckCircle className="w-6 h-6" />,
      label: "Completed",
      description: "Thank you for your order!",
      color: "bg-[#888]"
    },
    delayed: {
      icon: <Clock className="w-6 h-6" />,
      label: "Delayed",
      description: "Your order has been delayed. Please contact us at 847-652-0682.",
      color: "bg-amber-500"
    },
    cancelled: {
      icon: <Clock className="w-6 h-6" />,
      label: "Cancelled",
      description: "This order has been cancelled.",
      color: "bg-[#888]"
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      {/* Header */}
      <header className="bg-white border-b-2 border-[#DCD7CC]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#1A1A1A] hover:text-[#A61B1B] transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back</span>
          </Link>
          <h1 className="text-xl font-[700] text-[#1A1A1A]" style={{ fontFamily: 'Fraunces, serif' }}>
            Track Your Order
          </h1>
          <div className="w-16" />
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-12">
        {/* Search Form */}
        {!order && (
          <div className="bg-white border-2 border-[#1A1A1A] p-8 mb-8" data-testid="order-search-form">
            <h2 className="text-2xl font-[700] text-[#1A1A1A] mb-2" style={{ fontFamily: 'Fraunces, serif' }}>
              Find Your Order
            </h2>
            <p className="text-[#8A8A8A] mb-6">Enter your order ID to check the status</p>
            
            <form onSubmit={handleSearch} className="flex gap-4">
              <Input
                type="text"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value.toUpperCase())}
                placeholder="Enter Order ID (e.g., ABC12345)"
                className="flex-1 border-2 border-[#DCD7CC] focus:border-[#A61B1B] rounded-sm p-4 text-lg uppercase"
                data-testid="order-id-input"
              />
              <Button
                type="submit"
                disabled={loading || !orderId}
                className="bg-[#A61B1B] hover:bg-[#8B1515] text-white rounded-sm px-8"
                data-testid="search-order-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
              </Button>
            </form>
          </div>
        )}

        {/* Order Details */}
        {order && (
          <div className="space-y-6 animate-fade-in" data-testid="order-details">
            {/* Email Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3" data-testid="email-notice">
              <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <div>
                <p className="text-sm text-blue-800 font-medium">Confirmation email sent to {order.customer_email}</p>
                <p className="text-xs text-blue-600 mt-1">Don't see it? Check your spam/junk folder. Emails come from orders@ordermeatonoakton.com</p>
              </div>
            </div>

            {/* Order Header */}
            <div className="bg-white border-2 border-[#1A1A1A] p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-sm text-[#8A8A8A] uppercase tracking-wide">Order ID</p>
                  <h2 className="text-3xl font-[800] text-[#A61B1B]" style={{ fontFamily: 'Fraunces, serif' }} data-testid="order-id-display">
                    #{order.order_id}
                  </h2>
                </div>
                <Button
                  onClick={handleRefresh}
                  variant="outline"
                  className="border-2 border-[#DCD7CC] hover:border-[#A61B1B]"
                  disabled={refreshing}
                  data-testid="refresh-status-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
              
              <div className="text-sm text-[#8A8A8A]">
                Placed on {new Date(order.created_at).toLocaleString()}
              </div>
            </div>

            {/* Status Progress */}
            <div className="bg-white border-2 border-[#1A1A1A] p-6" data-testid="order-status-section">
              <h3 className="font-bold text-[#1A1A1A] mb-6 uppercase tracking-wide text-sm">Order Status</h3>
              
              {/* Current Status Display */}
              <div className={`${statusConfig[order.status]?.color || 'bg-gray-500'} text-white p-6 mb-8`}>
                <div className="flex items-center gap-4">
                  {statusConfig[order.status]?.icon}
                  <div>
                    <h4 className="text-xl font-bold" data-testid="current-status">
                      {statusConfig[order.status]?.label}
                    </h4>
                    <p className="opacity-90">{statusConfig[order.status]?.description}</p>
                  </div>
                </div>
              </div>

              {/* Progress Steps */}
              {order.status !== "cancelled" && (
                <div className="flex justify-between relative">
                  {["placed", "received", "processing", "ready"].map((status, index) => {
                    const currentIndex = getStatusIndex(order.status);
                    const isActive = index <= currentIndex;
                    const isCompleted = index < currentIndex;
                    
                    // Clear step labels for customers
                    const stepLabels = {
                      placed: "PLACED",
                      received: "CONFIRMED",
                      processing: "PREPARING",
                      ready: "READY"
                    };
                    
                    return (
                      <div key={status} className="flex flex-col items-center flex-1 relative">
                        {index < 3 && (
                          <div 
                            className={`absolute top-5 left-1/2 w-full h-1 ${isCompleted ? 'bg-[#4A7C59]' : 'bg-[#DCD7CC]'}`}
                          />
                        )}
                        <div 
                          className={`w-10 h-10 rounded-full flex items-center justify-center z-10 transition-all ${
                            isActive ? 'bg-[#4A7C59] text-white' : 'bg-[#DCD7CC] text-[#8A8A8A]'
                          }`}
                          data-testid={`progress-step-${status}`}
                        >
                          {isCompleted ? (
                            <CheckCircle className="w-5 h-5" />
                          ) : (
                            <span className="font-bold">{index + 1}</span>
                          )}
                        </div>
                        <span className={`mt-2 text-xs font-medium uppercase ${isActive ? 'text-[#1A1A1A]' : 'text-[#8A8A8A]'}`}>
                          {stepLabels[status]}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {order.status === "ready" && (
                <div className="mt-8 p-4 bg-[#E6F4EA] border-2 border-[#4A7C59] text-center">
                  <p className="text-[#065F46] font-bold text-lg">
                    Your order is ready! Please come to the meat counter.
                  </p>
                </div>
              )}
            </div>

            {/* Order Items */}
            <div className="bg-white border-2 border-[#1A1A1A] p-6" data-testid="order-items-section">
              <h3 className="font-bold text-[#1A1A1A] mb-4 uppercase tracking-wide text-sm">Order Items</h3>
              <div className="space-y-3">
                {order.items.map((item, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-[#DCD7CC] last:border-0">
                    <div>
                      <p className="font-medium text-[#1A1A1A]">{item.product_name}</p>
                      {item.notes && <p className="text-sm text-[#8A8A8A]">Note: {item.notes}</p>}
                    </div>
                    <span className="font-bold text-[#A61B1B]">
                      {item.quantity} {item.unit}
                    </span>
                  </div>
                ))}
              </div>
              {order.special_instructions && (
                <div className="mt-4 pt-4 border-t border-[#DCD7CC]">
                  <p className="text-sm text-[#8A8A8A]">Special Instructions:</p>
                  <p className="text-[#1A1A1A]">{order.special_instructions}</p>
                </div>
              )}
            </div>

            {/* Search Another Order */}
            <div className="text-center">
              <Button
                onClick={() => setOrder(null)}
                variant="outline"
                className="border-2 border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white rounded-sm"
                data-testid="search-another-btn"
              >
                Track Another Order
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackOrderPage;
